// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './HistoryStorageSite.module.scss';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getBrightnessFromColor, normalizeNumber } from 'util/commons';
import ResponsiveHistoryTable, {
  CustomColumn,
} from 'components/molecules/ResponsiveHistoryTable';
import CustomPagination from 'components/atoms/CustomPagination';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import Popover from 'components/atoms/Popover';
import { useFetchStorageSiteHistoryQuery } from 'apis/compressions_user/compressionUserApi';
import { StorageSiteHistoryItem } from 'util/Types';
import { getFormatDateWithDeviceTimezone } from 'ts/formatDate';
import {
  DATE_FORMAT,
  TEMPORARY_STORAGE_COLORS_PALETTE,
} from 'util/ConstantValues';

const PAGE_SIZE = 20;

export const HistoryStorageSite = ({
  onAppliedDateRangeChange,
}: {
  onAppliedDateRangeChange?: (range: [string, string] | undefined) => void;
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [dateRange, setDateRange] = useState<[string, string]>();
  const [appliedDateRange, setAppliedDateRange] = useState<[string, string]>();

  const { data, refetch } = useFetchStorageSiteHistoryQuery({
    page: currentPage + 1,
    take: PAGE_SIZE,
    dateFrom: appliedDateRange?.[0],
    dateTo: appliedDateRange?.[1],
  });

  const [historyItems, setHistoryItems] = useState<StorageSiteHistoryItem[]>(
    []
  );
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  useEffect(() => {
    setHistoryItems(data?.data ?? []);
  }, [data?.data]);

  const handleSearch = () => {
    const isSameRange =
      dateRange?.[0] === appliedDateRange?.[0] &&
      dateRange?.[1] === appliedDateRange?.[1];

    setCurrentPage(0);
    setAppliedDateRange(dateRange);
    onAppliedDateRangeChange?.(dateRange);

    if (isSameRange) {
      refetch();
    }
  };

  const columns: CustomColumn<StorageSiteHistoryItem>[] = useMemo(
    () => [
      {
        Header: t('histories.label_csv_received_date_and_time') as string,
        accessor: (row) => row.receivedDatetime,
        Cell: ({ value }) => <>{getFormatDateWithDeviceTimezone(value)}</>,
      },
      {
        Header: t('common_history_field.place') as string,
        accessor: (row) => row.fromPlace,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
      {
        Header: t('histories.label_item_name') as string,
        accessor: (row) => row.itemName,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
      {
        Header: t('common_history_field.material_name') as string,
        accessor: (row) => row.materialName,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
      {
        Header: t('common_history_field.weight') as string,
        accessor: (row) => row.weight,
        Cell: ({ value }) => (
          <>{normalizeNumber({ value: value as string }) + 'kg'}</>
        ),
      },
      {
        Header: t('storage_location_registration.receiving_method') as string,
        accessor: (row) => row.receivingMethod,
        Cell: ({ value }) => (
          <>
            {t(
              `histories.storage_receiving_method.${value}`,
              value as string
            ) || '-'}
          </>
        ),
      },
      {
        Header: t('histories.label_resource_id') as string,
        accessor: (row) => row.resourceId,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
      {
        Header: t('storage_location_registration.local_storage') as string,
        accessor: (row) => row.storageLocation,
        Cell: ({ row }) => {
          const loc = row.original.storageLocation;

          if (!loc) {
            return <>{t('storage_location_registration.not_set')}</>;
          }

          const bgColor =
            TEMPORARY_STORAGE_COLORS_PALETTE[
              Number(loc.color) % TEMPORARY_STORAGE_COLORS_PALETTE.length
            ];

          return (
            <span
              className={styles.storageLocationBadge}
              style={{
                backgroundColor: bgColor,
                color:
                  getBrightnessFromColor(bgColor) <= 100
                    ? '#ffffff'
                    : '#1b1b1b',
              }}
            >
              <Popover
                autoGen={{
                  lines: 1,
                  odd: 4,
                  wrapperStyle: {
                    width: 'fit-content',
                  },
                }}
                popUpStyle={{
                  fontSize: 18,
                }}
                positions={['top']}
                content={loc.name}
                containerStyle={{
                  maxWidth: '400px',
                  wordBreak: 'break-all',
                  zIndex: '1000',
                }}
              >
                <span>{loc.name}</span>
              </Popover>
            </span>
          );
        },
      },
      {
        Header: t(
          'storage_location_registration.storage_update_datetime'
        ) as string,
        accessor: (row) => row.storageUpdateDatetime,
        Cell: ({ value }) => (
          <>{value ? getFormatDateWithDeviceTimezone(value as string) : '-'}</>
        ),
      },
      {
        Header: t('histories.label_status') as string,
        accessor: (row) => row.status,
        Cell: ({ value }) => (
          <>
            {t(`histories.storage_site_status.${value}`, value as string) ||
              '-'}
          </>
        ),
      },
      {
        Header: t('storage_location_registration.shipment_date') as string,
        accessor: (row) => row.shipmentDate,
        Cell: ({ value }) => (
          <>
            {value
              ? getFormatDateWithDeviceTimezone(
                  value as string,
                  DATE_FORMAT.full_date
                )
              : '-'}
          </>
        ),
      },
    ],
    [t]
  );

  return (
    <div className={styles.historyStorageSite}>
      <div className={styles.header}>
        <TableFilterCalendar
          wrapperClassName={styles.dateFilter}
          containerClassName={styles.dateFilterInner}
          expanded={{
            placeholder: t('common.placeholder.select_date_range'),
            icon: <IconCalendarTablet />,
          }}
          portalPositionPoint={(ref) => ref}
          endDateRequired
          value={dateRange}
          onChange={setDateRange}
        />
        <button className={styles.searchButton} onClick={handleSearch}>
          {t('common.placeholder.search')}
        </button>
      </div>

      <div className={styles.content}>
        <ResponsiveHistoryTable<StorageSiteHistoryItem>
          data={historyItems}
          columns={columns}
          scrollResetDependencies={[currentPage]}
        />
      </div>

      {totalPages > 0 && (
        <div className={styles.paginationContainer}>
          <CustomPagination
            pageCount={totalPages}
            onPageChange={({ selected }) => setCurrentPage(selected)}
            forcePage={currentPage}
          />
        </div>
      )}
    </div>
  );
};
